/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=44%3A42
import type { PropsWithChildren, ReactNode } from 'react';
import React, { useState, useMemo } from 'react';
import { css } from '@emotion/react';
import type { TooltipProps } from '@mui/material/Tooltip';
import TooltipMUI from '@mui/material/Tooltip';
import type { SxProps } from '@mui/material';
import {
  ClickAwayListener,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import Typography, { TypographyVariant } from '../Typography';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
  type IPropsWithTestId,
} from '../../../constants/data-element-tracking-ids';
import type { ITheme } from '../../styles/theme';
import { useSettingBasedOnBreakpoints } from '../../../hooks/use-setting-based-on-breakpoints';
import { ElementZIndexOrdering } from '../../utils/element-z-iIndex-ordering';
import { UnstyledButton } from '../Button';
import { BoxWithId } from '../BoxWithId/BoxWithId';

const TooltipIcon = ({
  theme,
  parentElementId,
}: {
  theme: ITheme;
  parentElementId: IPropsWithTestId['dataElementId'];
}) => {
  const iconStyles = css`
    cursor: pointer;
    color: ${theme.colors.neutral};
  `;

  return (
    <FontAwesomeIcon
      css={iconStyles}
      icon={faCircleQuestion}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{
        [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${parentElementId}-tooltip-icon`,
      }}
    />
  );
};

const StyledTooltip = styled.div`
  max-width: 340px;
  width: 100%;
  background-color: #f2f7fe;
  box-shadow: 0px 4px 4px 0px #00000040;
  padding: 30px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  margin: 0;
  color: black;
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: ${ElementZIndexOrdering.TOOLTIP_FIXED};
  @media (max-width: 400px) {
    bottom: 24px;
    right: 24px;
    max-width: calc(100% - 108px);
  }
`;

interface IFixedTooltip extends IPropsWithTestId {
  children?: React.ReactNode;
  title: React.ReactNode;
}

const getTooltipButtonDataElementId = (
  parentElementId: IPropsWithTestId['dataElementId'],
) => `${parentElementId}-tooltip-button`;
const getTooltipBodyDataElementId = (
  parentElementId: IPropsWithTestId['dataElementId'],
) => `${parentElementId}-tooltip-body`;

const FixedTooltip = ({ children, title, dataElementId }: IFixedTooltip) => {
  const [isOpen, setOpen] = useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <BoxWithId dataElementId={dataElementId}>
        <div
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <UnstyledButton
            additionalStyles={{ width: 'fit-content', height: 'fit-content' }}
            type="button"
            onPress={() => setOpen((prev) => !prev)}
            dataElementId={getTooltipButtonDataElementId(dataElementId)}
          >
            {children}
          </UnstyledButton>
        </div>
        <Fade in={isOpen}>
          <StyledTooltip
            {...{
              [ELEMENT_ID_DATA_TEST_ATTRIBUTE]:
                getTooltipBodyDataElementId(dataElementId),
            }}
          >
            {title}
          </StyledTooltip>
        </Fade>
      </BoxWithId>
    </ClickAwayListener>
  );
};

interface IProps extends IPropsWithTestId {
  infoContent: ReactNode;
  theme: ITheme;
  placement?: TooltipProps['placement'];
  isAnchored?: boolean;
  small?: boolean;
  iconAfterWord?: boolean;
}

const Tooltip: React.FC<
PropsWithChildren<IProps & Omit<TooltipProps, 'title' | 'children'>>
> = ({
  theme,
  infoContent,
  placement,
  dataElementId,
  isAnchored = false,
  small = false,
  iconAfterWord = true,
  // children is used as a label or some element,
  // interaction with which shows useful content (infoContent)
  children = <TooltipIcon theme={theme} parentElementId={dataElementId} />,
  ...props
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const muiTheme = useTheme();
  const { isDesktop } = useSettingBasedOnBreakpoints(muiTheme);
  const userHasMousePointer = useMediaQuery('(pointer: fine)');

  const handleClose = () => {
    setOpen(false);
  };

  const handleIconClick = () => {
    setOpen((prev) => !prev);
  };

  const titleContent = (
    <Typography
      variant={TypographyVariant.SMALL_BODY_REGULAR}
      color={small ? theme.colors.secondary : theme.colors.grayscale.black}
      additionalStyles={{ wordWrap: 'normal' }}
      dataElementId={getTooltipBodyDataElementId(dataElementId)}
    >
      {infoContent}
    </Typography>
  );

  const tooltipStyles = useMemo<SxProps>(
    () => ({
      maxWidth: userHasMousePointer ? '338px' : '290px',
      zIndex: ElementZIndexOrdering.TOOLTIP_ANCHOR,
      '& .MuiTooltip-tooltip': small
        ? {
          backgroundColor: theme.colors.grayscale.white,
          border: `1px solid ${theme.colors.grayscale.outlineGrey}`,
          boxShadow: '0px 4px 4px 0px #0000001A',
          padding: '8px',
          whiteSpace: 'normal',
        }
        : {
          backgroundColor: theme.colors.neutralBackgroundColor,
          boxShadow: `0px ${userHasMousePointer ? 4 : 13}px ${
            userHasMousePointer ? 4 : 28
          }px 0px #00000040`,
          padding: isDesktop ? '30px' : '24px',
          whiteSpace: 'normal',
        },
    }),
    [userHasMousePointer, theme.colors, isDesktop, small],
  );

  const tooltipIconWrapperStyles = useMemo(
    () => ({
      display: 'inline-block',
      marginLeft: iconAfterWord ? '5px' : 0,
    }),
    [iconAfterWord],
  );

  if (isAnchored) {
    return userHasMousePointer ? (
      <TooltipMUI
        sx={tooltipStyles}
        placement={placement}
        componentsProps={{ popper: { sx: tooltipStyles } }}
        title={titleContent}
        {...{
          [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: dataElementId,
          [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId,
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        <BoxWithId
          style={tooltipIconWrapperStyles}
          dataElementId={getTooltipButtonDataElementId(dataElementId)}
        >
          {children}
        </BoxWithId>
      </TooltipMUI>
    ) : (
      <ClickAwayListener onClickAway={handleClose}>
        <BoxWithId
          style={tooltipIconWrapperStyles}
          dataElementId={dataElementId}
        >
          <TooltipMUI
            sx={tooltipStyles}
            placement={placement}
            componentsProps={{ popper: { sx: tooltipStyles } }}
            title={titleContent}
            open={open}
            PopperProps={{
              disablePortal: true,
            }}
            onClose={handleClose}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          >
            <div>
              <UnstyledButton
                onPress={handleIconClick}
                type="button"
                dataElementId={getTooltipButtonDataElementId(dataElementId)}
                additionalStyles={{ outline: 'none' }}
              >
                {children}
              </UnstyledButton>
            </div>
          </TooltipMUI>
        </BoxWithId>
      </ClickAwayListener>
    );
  }

  return (
    <FixedTooltip title={titleContent} dataElementId={dataElementId}>
      {children}
    </FixedTooltip>
  );
};

export default Tooltip;
